import React, { useState } from 'react';

/**
 * Rooms Page Component - Redesigned
 * Features:
 * - Image gallery layout
 * - Room description
 * - Amenities grid
 * - Hotel Rules
 * - Cancellation Policy
 */
const Rooms = () => {
  const [selectedImage, setSelectedImage] = useState(0);

  // Gallery images
  const galleryImages = [
    `${process.env.PUBLIC_URL}/inc/images/Royal_rooms_Napels_1.jpg`,
    `${process.env.PUBLIC_URL}/inc/images/Royal_rooms_Napels_2.jpg`,
    `${process.env.PUBLIC_URL}/inc/images/Royal_rooms_Napels_3.jpg`,
    `${process.env.PUBLIC_URL}/inc/images/Royal_rooms_Napels_4.jpg`,
    `${process.env.PUBLIC_URL}/inc/images/Royal_rooms_Napels_5.jpg`,
  ];

  // Amenities list
  const amenities = [
    { name: 'Air Conditioner', icon: '❄️' },
    { name: 'High Speed WiFi', icon: '📶' },
    { name: 'Strong Door Lock', icon: '🔒' },
    { name: 'Breakfast', icon: '🍳' },
    { name: 'Family Room', icon: '👨‍👩‍👧‍👦' },
    { name: '24-Hour Front Desk', icon: '🕐' },
    { name: 'Table Space', icon: '🪑' },
    { name: 'Double Bed', icon: '🛏️' },
  ];

  // Hotel Rules
  const hotelRules = [
    'Check-in time from 12 PM, check-out by 10 AM',
    'Check-in with valid ID required.',
    'Check-out on time to avoid fees.',
    'No extra guests without approval.',
    'Keep noise low, especially at night.',
    'No smoking indoors, use designated areas.',
    'Pets are not allowed inside hotel.',
    'Guests pay for any property damage.',
    'Lock rooms, hotel not responsible.',
    'No illegal substances or hazardous items.',
    'Follow rules for hotel facilities use.',
    'Visitors allowed only during permitted hours.',
    'Bills must be settled before departure.',
    'Cancellation policy applies to bookings.',
    'No commercial photography without permission.',
    'Follow local laws and hotel policies.',
  ];

  return (
    <main className="pt-10 sm:pt-14">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center bg-cover bg-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 26, 46, 0.7), rgba(26, 26, 46, 0.7)), url('${process.env.PUBLIC_URL}/inc/images/bg.webp')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg mb-4">
            Our Luxury Family Room
          </h1>
          <p className="text-lg sm:text-xl opacity-90 max-w-2xl mx-auto">
            Ideal for those traveling with loved ones, offering ample space and a warm ambiance
          </p>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Main Image */}
          <div className="mb-6 rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={galleryImages[selectedImage]}
              alt={`Room view ${selectedImage + 1}`}
              className="w-full h-[500px] sm:h-[600px] lg:h-[700px] object-cover"
            />
          </div>

          {/* Thumbnail Gallery */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`relative overflow-hidden rounded-lg shadow-md transition-all duration-300 ${
                  selectedImage === index
                    ? 'ring-4 ring-primary-bronze scale-105'
                    : 'hover:scale-105 hover:shadow-lg'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-32 sm:h-40 object-cover"
                />
                {selectedImage === index && (
                  <div className="absolute inset-0 bg-primary-bronze/20" />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Room Description Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary-navy mb-6 pb-4 border-b-4 border-primary-bronze">
              Our Luxury Family Room
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
              Our Luxury Family Room is ideal for those traveling with loved ones, offering ample space and a warm ambiance. This well-designed room includes a cozy double bed and twin single beds, ensuring comfort for all. With stylish décor, a private bathroom, and essential amenities, this room is perfect for a relaxing family getaway.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl">🛏️</span>
                <div>
                  <p className="font-semibold text-primary-bronze">Double Bed</p>
                  <p className="text-sm text-gray-600">Cozy and comfortable</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl">🛏️</span>
                <div>
                  <p className="font-semibold text-primary-bronze">Twin Single Beds</p>
                  <p className="text-sm text-gray-600">Perfect for families</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl">🚿</span>
                <div>
                  <p className="font-semibold text-primary-bronze">Private Bathroom</p>
                  <p className="text-sm text-gray-600">Fully equipped</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl">🎨</span>
                <div>
                  <p className="font-semibold text-primary-bronze">Stylish Décor</p>
                  <p className="text-sm text-gray-600">Modern and elegant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-primary-navy mb-8 sm:mb-12 text-center">
            Amenities
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {amenities.map((amenity, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">{amenity.icon}</div>
                  <h3 className="font-semibold text-primary-bronze text-sm sm:text-base">
                    {amenity.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel Rules Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary-navy mb-8 pb-4 border-b-4 border-primary-bronze">
              Hotel Rules
            </h2>
            <div className="space-y-4">
              {hotelRules.map((rule, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <span className="text-primary-bronze font-bold text-xl mt-1 flex-shrink-0">✓</span>
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{rule}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cancellation Policy Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-primary-bronze to-[#7a5230] rounded-2xl shadow-2xl p-8 sm:p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 pb-4 border-b-4 border-white/30">
              Cancellation Policy – Royal Rooms B&B
            </h2>
            <p className="text-base sm:text-lg leading-relaxed mb-8 opacity-95">
              We understand that travel plans can change. To ensure a smooth experience for all our guests, we have established the following cancellation policy:
            </p>
            
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-3">
                  <span className="text-2xl">✅</span>
                  Free Cancellation
                </h3>
                <p className="text-base sm:text-lg opacity-95">
                  Guests can cancel their reservation up to 72 hours before check-in (local time) at no charge.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  Late Cancellation
                </h3>
                <p className="text-base sm:text-lg opacity-95">
                  If a cancellation is made less than 72 hours before check-in, a cancellation fee of 50% of the total booking amount will be charged.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-3">
                  <span className="text-2xl">❌</span>
                  No-show Policy
                </h3>
                <p className="text-base sm:text-lg opacity-95">
                  If a guest does not check in on the scheduled arrival date and does not notify us in advance, 100% of the total booking amount will be charged.
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
              <p className="text-base sm:text-lg font-semibold mb-2">
                For any modifications or cancellations, please contact us at:
              </p>
              <a
                href="mailto:info@royalrooms.it"
                className="text-white text-lg sm:text-xl font-bold hover:underline inline-flex items-center gap-2"
              >
                📧 info@royalrooms.it
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Book Now CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary-bronze to-[#7a5230]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Book Your Stay?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8">
            Experience the comfort and luxury of our Family Room
          </p>
          <a
            href="https://royalroomsbbnapoli.kross.travel/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary-bronze px-8 sm:px-12 py-4 sm:py-5 rounded-lg font-bold text-lg sm:text-xl shadow-2xl hover:bg-cream hover:-translate-y-2 hover:shadow-3xl transition-all duration-300 transform"
          >
            Book Your Room Now
          </a>
        </div>
      </section>
    </main>
  );
};

export default Rooms;
